import type { Cart, ProductDetail, PublicOrderStatus } from '@sshop/domain';

import { env } from '@/shared/config/env';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & {
      queue?: unknown[][];
      loaded?: boolean;
      version?: string;
    };
  }
}

type MetaPixelQueue = ((...args: unknown[]) => void) & {
  queue?: unknown[][];
  loaded?: boolean;
  version?: string;
};

const CONSENT_KEY = 'sshop_tracking_consent';
let initialized = false;
let metaInitialized = false;
let lastPageView = '';
let lastProductView = '';
let lastCartView = '';
let lastCheckoutView = '';

function push(payload: unknown) {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(payload);
}

function gtag(...args: unknown[]) {
  push(args);
}

function hasConsent() {
  try {
    return window.localStorage.getItem(CONSENT_KEY) === 'granted';
  } catch {
    return false;
  }
}

function loadMetaPixel() {
  if (!env.META_PIXEL_ID || metaInitialized) return;
  metaInitialized = true;
  const fbq: MetaPixelQueue = (...args: unknown[]) => {
    (fbq.queue = fbq.queue ?? []).push(args);
  };
  fbq.loaded = true;
  fbq.version = '2.0';
  window.fbq = fbq;
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  fbq('init', env.META_PIXEL_ID);
}

export function initializeAnalytics() {
  if (initialized) return;
  initialized = true;
  window.gtag = gtag;
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });
  if (env.GTM_ID) {
    push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(env.GTM_ID)}`;
    document.head.appendChild(script);
  }
  if (hasConsent()) setTrackingConsent(true);
}

export function trackingConsentChoice(): 'granted' | 'denied' | null {
  try {
    const value = window.localStorage.getItem(CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
}

export function setTrackingConsent(granted: boolean) {
  const value = granted ? 'granted' : 'denied';
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch {
    /* storage may be disabled */
  }
  gtag('consent', 'update', {
    analytics_storage: value,
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
  if (granted) {
    loadMetaPixel();
    window.fbq?.('consent', 'grant');
  } else {
    window.fbq?.('consent', 'revoke');
  }
}

function meta(event: string, payload?: Record<string, unknown>, eventId?: string) {
  if (!hasConsent()) return;
  loadMetaPixel();
  window.fbq?.('track', event, payload ?? {}, eventId ? { eventID: eventId } : undefined);
}

export function trackPageView(path: string) {
  if (lastPageView === path) return;
  lastPageView = path;
  push({
    event: 'page_view',
    page_location: new URL(path, window.location.origin).href,
    page_title: document.title,
  });
  meta('PageView');
}

function productItem(product: ProductDetail, quantity = 1) {
  return {
    item_id: String(product.sku),
    item_name: product.title,
    item_brand: product.brand ?? undefined,
    price: product.price.amount,
    quantity,
  };
}

function cartItems(cart: Cart) {
  return cart.items.map((item) => ({
    item_id: item.sku,
    item_name: item.title,
    price: item.unitPrice.amount,
    quantity: Number(item.quantity),
  }));
}

export function trackViewItem(product: ProductDetail) {
  const viewKey = `${window.location.pathname}:${product.sku}`;
  if (lastProductView === viewKey) return;
  lastProductView = viewKey;
  const item = productItem(product);
  push({ ecommerce: null });
  push({
    event: 'view_item',
    ecommerce: { currency: 'GEL', value: product.price.amount, items: [item] },
  });
  meta('ViewContent', {
    content_ids: [String(product.sku)],
    content_type: 'product',
    content_name: product.title,
    value: product.price.amount,
    currency: 'GEL',
  });
}

export function trackAddToCart(product: ProductDetail, quantity: number) {
  const value = product.price.amount * quantity;
  push({ ecommerce: null });
  push({
    event: 'add_to_cart',
    ecommerce: { currency: 'GEL', value, items: [productItem(product, quantity)] },
  });
  meta('AddToCart', {
    content_ids: [String(product.sku)],
    content_type: 'product',
    contents: [{ id: String(product.sku), quantity }],
    value,
    currency: 'GEL',
  });
}

export function trackViewCart(cart: Cart) {
  if (lastCartView === cart.updatedAt) return;
  lastCartView = cart.updatedAt;
  push({ ecommerce: null });
  push({
    event: 'view_cart',
    ecommerce: { currency: 'GEL', value: cart.totals.total.amount, items: cartItems(cart) },
  });
}

export function trackBeginCheckout(cart: Cart) {
  if (lastCheckoutView === cart.updatedAt) return;
  lastCheckoutView = cart.updatedAt;
  const items = cartItems(cart);
  push({ ecommerce: null });
  push({
    event: 'begin_checkout',
    ecommerce: { currency: 'GEL', value: cart.totals.total.amount, items },
  });
  meta('InitiateCheckout', {
    content_ids: items.map((item) => item.item_id),
    content_type: 'product',
    contents: items.map((item) => ({ id: item.item_id, quantity: item.quantity })),
    value: cart.totals.total.amount,
    currency: 'GEL',
  });
}

export function trackPaymentSelection(cart: Cart, paymentType: string) {
  push({ ecommerce: null });
  push({
    event: 'add_payment_info',
    ecommerce: {
      currency: 'GEL',
      value: cart.totals.total.amount,
      payment_type: paymentType,
      items: cartItems(cart),
    },
  });
}

export function trackPurchaseOnce(order: PublicOrderStatus) {
  if (!['paid', 'processing', 'shipped', 'delivered'].includes(order.status)) return;
  const key = `sshop_purchase_${order.id}`;
  try {
    if (window.localStorage.getItem(key)) return;
  } catch {
    /* continue without persistence */
  }
  const items = order.items.map((item) => ({
    item_id: item.itemId,
    item_name: item.itemName,
    price: item.price,
    quantity: item.quantity,
  }));
  push({ ecommerce: null });
  push({
    event: 'purchase',
    ecommerce: {
      transaction_id: order.id,
      currency: order.currency,
      value: order.value,
      shipping: order.shipping,
      items,
    },
  });
  meta(
    'Purchase',
    {
      content_ids: items.map((item) => item.item_id),
      content_type: 'product',
      contents: items.map((item) => ({ id: item.item_id, quantity: item.quantity })),
      value: order.value,
      currency: order.currency,
    },
    `Purchase.${order.id}`,
  );
  try {
    window.localStorage.setItem(key, new Date().toISOString());
  } catch {
    /* storage may be disabled */
  }
}
